import { getLiveStatusRuns } from "../helpers/live-status-runs";
import { FC, useMemo, useState, memo } from "react";
import { createPortal } from "react-dom";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { Box, IconButton } from "@mui/material";
import { makeSxStyles } from "ui";
import {
  AdminGetRoutesLocationsResDto,
  RouteStartingPointDto,
} from "service/api/estabrookSchemas";
import {
  SkippedDirectionArrow,
  ForwardDirectionArrow,
  BackwardsDirectionArrow,
} from "ui/icons";
import useRoutePlannerStore, {
  AddressStatus,
} from "../../../../state/route-planner-store";
import DropZone from "./drop-zone";
import { Delete } from "@mui/icons-material";
import { RouteStatus } from "../helpers/route-status.enum";
import {
  isSkippedStop,
  stopServiceMinutes,
} from "../helpers/stop-service-minutes";
import {
  calculateLeftPosition,
  STARTING_POINT_OFFSET,
} from "../helpers/timetable";
import DriverStartingPointDetails from "../driver-starting-point-details";
import { ClickedAddress } from "../../../../shared/hooks/use-address-click-handler";
import useTimetableZoomStore from "../../../../state/timetable-zoom-store";
import {
  useRealtimeTimeAdjustments,
  TimeAdjustments,
} from "../../../../hooks/use-realtime-time-adjustments";
import { computeCompletedStackShifts } from "../helpers/completed-stack-shifts";
import { useReportRouteAdjustments } from "../../../../hooks/use-route-planner-adjustments";

interface OrderLayoutProps {
  color: string;
  routeId: string;
  isVirtualRoute: boolean;
  locations: AdminGetRoutesLocationsResDto[];
  onClickedAddress: (value: ClickedAddress) => void;
  handleMouseEnter: (orderId: string, event: React.MouseEvent) => void;
  handleMouseLeave: () => void;
  onDeleteClick: (routeId: string) => void;
  currentRouteStatus?: RouteStatus;
  routeStartTime: string;
  routeDate: string;
  startingPoint?: RouteStartingPointDto;
  driverFullName?: string;
  isUnassignedRoute?: boolean;
  timelineReference?: { min: string | null; max: string | null };
  // Real DOM node for the Skipped table cell in this row — content always
  // portals into it so it sticks correctly (unlike position: sticky from inside
  // the Addresses cell which detaches past that cell's own edge).
  skippedPortalTarget?: HTMLElement | null;
  isDraggingGlobal?: boolean;
  onAdjustmentsChange?: (
    routeId: string,
    adjustments: Record<string, TimeAdjustments>,
  ) => void;
}

export const OrderLayout: FC<OrderLayoutProps> = ({
  color,
  routeId,
  isVirtualRoute,
  locations,
  onClickedAddress,
  handleMouseEnter,
  handleMouseLeave,
  onDeleteClick,
  currentRouteStatus,
  routeStartTime,
  routeDate,
  startingPoint,
  driverFullName,
  isUnassignedRoute = false,
  timelineReference,
  skippedPortalTarget = null,
  isDraggingGlobal = false,
  onAdjustmentsChange,
}) => {
  const styles = useStyles();
  // Use selectors to only subscribe to specific store values, not the entire store
  const minAndMaxStartTimeOfRoutes = useRoutePlannerStore(
    (state) => state.minAndMaxStartTimeOfRoutes,
  );
  const zoomLevel = useTimetableZoomStore((state) => state.zoomLevel);

  // Use passed timeline reference if available, otherwise fall back to store
  const actualTimelineReference =
    timelineReference || minAndMaxStartTimeOfRoutes;

  // State for managing line hover
  const [hoveredLine, setHoveredLine] = useState<{
    source: string;
    destination: string;
  } | null>(null);

  const handleLineHover = (
    sourceLocationId: string,
    destinationLocationId: string,
  ) => {
    setHoveredLine({
      source: sourceLocationId,
      destination: destinationLocationId,
    });
  };

  const handleLineHoverEnd = () => {
    setHoveredLine(null);
  };

  const filteredItems = useMemo(() => {
    const statuses = [
      AddressStatus.Regular,
      AddressStatus.Delayed,
      AddressStatus.Skipped,
    ];
    return statuses.reduce(
      (acc, status) => {
        acc[status] =
          locations?.filter((loc) => loc.order?.status === status) || [];
        return acc;
      },
      {} as Record<AddressStatus, typeof locations>,
    );
  }, [locations]);

  // Create a comprehensive list of all locations in sequence order for cross-zone navigation
  const allLocationsSorted = useMemo(() => {
    if (currentRouteStatus === RouteStatus.Live)
      return locations.filter((location) => !isSkippedStop(location));
    return Object.values(filteredItems).flat();
  }, [filteredItems, locations, currentRouteStatus]);

  // Use real-time time adjustments hook
  const { adjustments } = useRealtimeTimeAdjustments({
    locations,
    routeStartTime,
    routeDate,
    currentRouteStatus,
  });

  useReportRouteAdjustments(routeId, adjustments, onAdjustmentsChange);

  // The next stop in the order the server lists a route's stops (the order its
  // cumulative service and travel times follow). Skipped stops aren't driven,
  // so they are nobody's next stop.
  const drivenLocations = useMemo(
    () => locations.filter((location) => !isSkippedStop(location)),
    [locations],
  );

  const nextLocationMap = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    drivenLocations.forEach((location, index) => {
      if (index < drivenLocations.length - 1) {
        map[location.locationId] = drivenLocations[index + 1].locationId;
      }
    });
    return map;
  }, [drivenLocations]);

  const nextLocationDataMap = useMemo(() => {
    const map: Record<string, AdminGetRoutesLocationsResDto | undefined> = {};
    drivenLocations.forEach((location, index) => {
      if (index < drivenLocations.length - 1) {
        map[location.locationId] = drivenLocations[index + 1];
      }
    });
    return map;
  }, [drivenLocations]);

  // In the live phase travelDetails.serviceTimeMin is cumulative, so each
  // stop's own service time is its total minus the previous stop's.
  const actualServiceTimeMap = useMemo((): Record<
    string,
    number | undefined
  > => {
    if (currentRouteStatus !== RouteStatus.Live) {
      return {};
    }
    return stopServiceMinutes(locations);
  }, [locations, currentRouteStatus]);

  // Stacks can cross status groups when completions happen out of sequence.
  const completedStackShifts = useMemo(
    () =>
      currentRouteStatus === RouteStatus.Live
        ? computeCompletedStackShifts(drivenLocations, {
            adjustments,
            actualServiceTimeMap,
            zoomLevel,
            currentRouteStatus,
          })
        : undefined,
    [
      drivenLocations,
      adjustments,
      actualServiceTimeMap,
      zoomLevel,
      currentRouteStatus,
    ],
  );

  // Use the same departure delay as the hook, including after a fresh mount.
  const firstLocation = allLocationsSorted[0];
  const firstTravel = firstLocation?.travelDetails?.travelTimeBetweenLocations;
  const firstArrival = firstLocation?.travelDetails?.estimatedTimeMinutes;
  const adjustedFirstArrival =
    firstLocation &&
    adjustments[firstLocation.locationId]?.adjustedEstimatedTimeMinutes;
  const travelTimeFromStartingPoint =
    firstTravel === undefined
      ? undefined
      : firstTravel +
        (adjustedFirstArrival !== undefined && firstArrival !== undefined
          ? Math.max(0, adjustedFirstArrival - firstArrival)
          : 0);

  const statusDropZoneConfigs = [
    {
      idSuffix: "regular",
      items: filteredItems[AddressStatus.Regular],
      icon: isUnassignedRoute ? undefined : <ForwardDirectionArrow />,
    },
    {
      idSuffix: "delayed",
      items: filteredItems[AddressStatus.Delayed],
      icon: isUnassignedRoute ? undefined : <BackwardsDirectionArrow />,
    },
    // Skipped orders always render in the dedicated sticky column below,
    // not inline here, regardless of route status.
  ];

  // Live stops follow actual completion order, which can cross regular and
  // delayed groups. Keep contiguous groups so their drag status and icons are
  // preserved without moving a finished delayed stop back into the future.
  const dropZoneConfigs = useMemo(() => {
    if (currentRouteStatus !== RouteStatus.Live) return statusDropZoneConfigs;
    return getLiveStatusRuns(locations).map(({ status, items }, index) => ({
      idSuffix: `${index}-${status}`,
      items,
      icon: !isUnassignedRoute ? (
        status === AddressStatus.Delayed ? (
          <BackwardsDirectionArrow />
        ) : items.length === 0 ? (
          <ForwardDirectionArrow />
        ) : undefined
      ) : undefined,
    }));
  }, [locations, currentRouteStatus, isUnassignedRoute, filteredItems]);

  const allItems = useMemo(
    () =>
      [
        ...dropZoneConfigs.flatMap(({ items }) => items),
        ...filteredItems[AddressStatus.Skipped],
      ].map((item) => item.locationId),
    [dropZoneConfigs, filteredItems],
  );

  // Find the first location that will actually be rendered across all dropzones
  const firstLocationToRender = useMemo(() => {
    for (const config of dropZoneConfigs) {
      if (config.items && config.items.length > 0) {
        return config.items[0].locationId;
      }
    }
    return null;
  }, [dropZoneConfigs]);

  const lastDropZoneWithItemsIndex = [...dropZoneConfigs]
    .reverse()
    .findIndex(({ items }) => items && items?.length > 0);
  const lastDropZoneIndex =
    lastDropZoneWithItemsIndex === -1
      ? -1
      : dropZoneConfigs.length - 1 - lastDropZoneWithItemsIndex;

  const leftPosition = calculateLeftPosition(
    routeStartTime,
    actualTimelineReference.min,
    undefined, // Use default TIMETABLE_ITEM_WIDTH
    zoomLevel,
  );

  // Shared between the collapsed (inline) and expanded (portaled) renderings below.
  const skippedContent = (
    <>
      {filteredItems[AddressStatus.Skipped]?.length > 0 && (
        <Box sx={styles.skippedIconContainer}>
          <SkippedDirectionArrow />
        </Box>
      )}
      <DropZone
        dropZoneDetails={{
          id: `${routeId}-skipped`,
          color: "skipped",
          items: filteredItems[AddressStatus.Skipped],
          routeId,
        }}
        handleMouseEnter={handleMouseEnter}
        onClickedAddress={onClickedAddress}
        handleMouseLeave={handleMouseLeave}
        isLastItem={true}
        travelTimeFromStartingPoint={undefined} // Skipped items don't need starting point travel time
        onLineHover={handleLineHover}
        onLineHoverEnd={handleLineHoverEnd}
        hoveredLineSource={hoveredLine?.source}
        hoveredLineDestination={hoveredLine?.destination}
        nextLocationMap={nextLocationMap}
        actualServiceTimeMap={actualServiceTimeMap}
        adjustments={adjustments}
        isUnassignedRoute={isUnassignedRoute}
        firstLocationToRender={firstLocationToRender}
        isDraggingGlobal={isDraggingGlobal}
      />
    </>
  );

  return (
    <SortableContext items={allItems} strategy={rectSortingStrategy}>
      <Box sx={styles.orderLayoutWrapper}>
        <Box
          sx={{
            ...styles.container,
            overflow: "hidden",
            ...(currentRouteStatus === RouteStatus.Live && {
              "& > :nth-of-type(2)": { flexGrow: 0 },
            }),
            // Apply positioning in all phases, not just Build/Live
            // Subtract offset to move the starting point left of the timeline start (position 0)
            left: leftPosition - STARTING_POINT_OFFSET,
            maxWidth: `calc(100% - ${leftPosition - STARTING_POINT_OFFSET}px)`,
          }}
        >
          {dropZoneConfigs.flatMap(({ idSuffix, items, icon }, index) => {
            const isLastItem =
              index === lastDropZoneIndex && items && items?.length > 0;
            const dropZoneDetails = {
              id: `${routeId}${currentRouteStatus === RouteStatus.Live ? "::" : "-"}${idSuffix}`,
              color,
              icon,
              items,
              routeId,
            };

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
                key={`${routeId}-${idSuffix}`}
              >
                {index === 0 &&
                  currentRouteStatus !== RouteStatus.Planning &&
                  !isUnassignedRoute && (
                    <DriverStartingPointDetails
                      startingPoint={startingPoint}
                      driverFullName={driverFullName}
                      currentRouteStatus={currentRouteStatus}
                      routeStartTime={routeStartTime}
                      locations={locations}
                    />
                  )}
                <DropZone
                  dropZoneDetails={dropZoneDetails}
                  handleMouseEnter={handleMouseEnter}
                  onClickedAddress={onClickedAddress}
                  handleMouseLeave={handleMouseLeave}
                  currentRouteStatus={currentRouteStatus}
                  isLastItem={isLastItem}
                  travelTimeFromStartingPoint={travelTimeFromStartingPoint}
                  onLineHover={handleLineHover}
                  onLineHoverEnd={handleLineHoverEnd}
                  hoveredLineSource={hoveredLine?.source}
                  hoveredLineDestination={hoveredLine?.destination}
                  nextLocationMap={nextLocationMap}
                  nextLocationDataMap={nextLocationDataMap}
                  actualServiceTimeMap={actualServiceTimeMap}
                  adjustments={adjustments}
                  completedStackShifts={completedStackShifts}
                  isUnassignedRoute={isUnassignedRoute}
                  firstLocationToRender={firstLocationToRender}
                  isDraggingGlobal={isDraggingGlobal}
                />
              </Box>
            );
          })}

          {isVirtualRoute && locations?.length === 0 && (
            <IconButton
              className="icon-button"
              sx={styles.deleteButton}
              onClick={() => onDeleteClick(routeId)}
            >
              <Delete sx={styles.deleteIcon} />
            </IconButton>
          )}
        </Box>
        {/* Skipped Orders column renders in every route status. Content
            always portals into the real Skipped table cell so it can never
            detach on scroll — the cell is always sticky (see route-planner-table.tsx),
            so it sticks correctly regardless of collapse state. */}
        {skippedPortalTarget &&
          createPortal(
            <Box sx={styles.skippedOrdersPortalContent}>{skippedContent}</Box>,
            skippedPortalTarget,
          )}
      </Box>
    </SortableContext>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    minWidth: "100px",
    position: "relative",
    "& > :first-of-type": {
      flexShrink: 0,
    },
    "& > :nth-of-type(2)": {
      flexGrow: 1,
    },
  },
  orderLayoutWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    overflow: "visible", // Allow skipped items to overflow the wrapper
  },
  // Fills the Skipped table cell it's portaled into — the cell handles
  // sticky positioning, so no position/right/z-index needed here.
  skippedOrdersPortalContent: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    overflow: "visible",
    pointerEvents: "auto",
  },
  skippedIconContainer: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  deleteButton: {
    display: "none",
    marginLeft: "auto",
    width: "32px",
    height: "32px",
  },
}));

export default memo(OrderLayout);
